import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { IDrop } from '../../../types/reducerTypes'
import { useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectTaskSubmissions,
  selectXpData,
} from '../../../modules/missionControl/selectors'
import styled, { css } from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import EmbeddableModal from '../../../shared_components/EmbeddableModal'
import { SubmissionsByTask, Task } from './types'
import ImageVerificationTask from './Types/ImageVerification'
import {
  filterDraftsAndEmptyObjectsFromTasks,
  groupTasksAndOrder,
} from '../../../hooks/useMissionControlPoolStateManagement'
import { Check as checkIconSrc } from '../../../shared_components/Missions/assets'
import TwitterTask from './Types/Twitter'
import TelegramTask from './Types/Telegram'
import DiscordTask from './Types/Discord'
import OnChainTask from './Types/OnChain'
import EmailTask from './Types/Email'
import TickUpNumber from '../../../shared_components/TickUpNumber'
import { isEmpty, isNil, isNotNil } from 'ramda'
import ProgressBar from '../../Home/parts/progress-bar'
import RollupTaskCard from './Types/Rollup'
import RecurringTaskCard from './Types/Recurring'
import ReferralTask from './Types/Referral'
import {
  Info,
  VariableRewardText,
} from '../../../shared_components/Missions/styles'
import FormTaskCard from './Types/Form'
import MissionAction, {
  getMissionTaskType,
  useMissionActionText,
  useStartMission,
} from '../../../shared_components/Missions/MissionAction'
import { useGetFormattedTaskRewardLabels } from '../../../shared_components/Missions/TaskAllocationAmount'
import { useCallback, useEffect, useMemo } from 'react'
import useProjectStatus from '../../../shared_components/Missions/useProjectStatus'
import TaskDescription from '../../../shared_components/Missions/TaskDescription'
import { Tooltip } from 'react-tooltip'
import GraphIndexerTask from './Types/GraphIndexer'
import {
  ManualApiRewardMetaData,
  NewTaskType,
} from '../../../shared_components/Missions/types'
import VerifyWalletTask from './Types/VerifyWallet'
import ZapValueIndexTask from './Types/ZapValueIndex'
import { ZapValueIndexTaskCard } from '../../../shared_components/Missions/Types/ZapValueIndex'
import FadeIn from '../../../shared_components/Animation/FadeIn'
import Leaderboard from '../Leaderboard'
import DropAbout from '../DropAbout'
import {
  H3,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import SharedTaskCard from '../../../shared_components/Library/Card/SharedTaskCard'
import { useShouldShowForThreshold } from '../../../theme'
import { UserProgress } from '../DropOverview'
import routes, { links } from '../../../router/routes'
import humanizeDuration from 'humanize-duration'
import { DropStatus, getDropDateStatus } from '../helpers'
import Separator from '../../../shared_components/Library/Separator'
import { boxGapGuttersCssRules } from '../../../shared_components/Header/styles'
import ManualAPIRewardTask from './Types/ManualApiReward'

const Container = styled(Box)``

const TaskContainer = styled(Box)`
  flex-wrap: wrap;
`

const ChapterView = ({
  drop,
  chapterNumber,
}: {
  drop: IDrop
  chapterNumber: number
}) => {
  const currentProject = useSelector(selectCurrentProject)
  const taskSubmissions = useSelector(selectTaskSubmissions)
  const tasks = currentProject?.tasks ?? []

  const location = useLocation()

  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)

  const updateSearchParam = (paramName: string, value: string) => {
    searchParams.set(paramName, value)
    history.push(`${location.pathname}?${searchParams.toString()}`)
  }

  const activeMission = searchParams.get('active_mission')

  function dismissModal() {
    updateSearchParam('active_mission', '')
  }

  const taskId = Number(activeMission ?? 0)

  const [activeTasks, , completedTasks, expiredTasks] = groupTasksAndOrder(
    filterDraftsAndEmptyObjectsFromTasks(tasks)
      .slice()
      .sort((a, b) => b.id - a.id) ?? [],
    taskSubmissions ?? ({} as SubmissionsByTask),
  )

  const isFormMission = !!activeTasks.find(
    (task) => !!task.subtasks?.[0]?.formMissionId,
  )

  const { start, end } = drop?.dropSettings ?? {}
  const projectStatus = useProjectStatus({ start, end })
  const isExpired = new Date(end).getTime() < new Date().getTime()
  const isTba = getDropDateStatus(drop) === DropStatus.TBA
  const isComingSoon = getDropDateStatus(drop) === DropStatus.ComingSoon
  const isLive = getDropDateStatus(drop) === DropStatus.Live
  const isClosed = getDropDateStatus(drop) === DropStatus.Closed

  const slug = drop?.slug
  const dropId = drop?.id
  const { params } = useRouteMatch() as { params: { chapter: string } }
  useEffect(() => {
    if (isTba || isComingSoon || params?.chapter !== String(dropId)) {
      history.replace(links.drops.slug(slug))
    }
  }, [dropId, history, isComingSoon, isTba, params?.chapter, slug])

  const endMs = end ? new Date(end).getTime() : 0
  const nowMs = new Date().getTime()
  const endIntervalBetween = endMs > nowMs ? endMs - nowMs : 0
  const endTimeLeft = end
    ? humanizeDuration(endIntervalBetween, {
        language: 'en',
        units: ['d', 'h', 'm'],
        round: true,
        largest: 1,
      })
    : ''

  const isAtLeastExpandedDisplaySize = useShouldShowForThreshold('expanded')

  const multiplierMissions = activeTasks
    .filter((task) => task.multiplierReward > 0)
    .sort((a, b) => b.multiplierReward - a.multiplierReward)
  const activeTasksWithoutMultiplierMission = activeTasks.filter(
    (task) => !task.multiplierReward,
  )
  return (
    <FadeIn fullWidth>
      <ProjectInformationBox
        ac
        marginTop={isAtLeastExpandedDisplaySize ? 48 : 40}
      >
        <Box dc>
          <Box dc g={16} marginBottom={16}>
            <H3 s="XL" fw="semibold">
              Chapter {chapterNumber}
            </H3>
            <Span s="L" c="text-secondary">
              {drop.about.description}
            </Span>
            <Box dc g={6}>
              <Box jfe>
                {isLive ? (
                  <Box jsb ac>
                    <Span s="S" c="text-secondary" fw="regular">
                      Ends in
                    </Span>
                    <Strong
                      s="S"
                      c={
                        endTimeLeft.includes('1 hour') ||
                        endTimeLeft.includes('minute') ||
                        endTimeLeft.includes('second') ||
                        endIntervalBetween <= 0
                          ? 'text-danger'
                          : 'text-primary'
                      }
                      fw="semibold"
                    >
                      {endTimeLeft}
                    </Strong>
                  </Box>
                ) : isClosed ? (
                  <Span s="S" c="text-secondary">
                    Closed
                  </Span>
                ) : null}
              </Box>
              <Separator />
              <Box key="rewards" ac>
                <Box>
                  <Span s="S" c="text-secondary" fw="regular">
                    Rewards
                  </Span>
                </Box>
                <Box fu aw g={6} afe>
                  {drop.dropSettings.rewardsSettings?.map(
                    ({ name, image, totalSupplyForSale }, index) => (
                      <RewardPill fu aw g={4} key={`reward-${index}`} ac>
                        {image?.url && (
                          <img
                            className="reward-img"
                            src={image.url}
                            alt={name}
                            height={16}
                            width={16}
                          />
                        )}
                        <Span s="S" c="text-primary" fw="semibold">
                          {totalSupplyForSale.toLocaleString()}
                        </Span>
                      </RewardPill>
                    ),
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <UserProgress
            showGetMultiplierButton
            showTotalXp
            showMissionsAvailable={false}
          />
        </Box>
        <ProjectImageBox fu aw>
          <img
            src={
              drop.media?.projectThumbnail?.url ??
              drop.media?.topBanner?.url ??
              ''
            }
            alt="drop thumbnail"
          />
        </ProjectImageBox>
      </ProjectInformationBox>
      <Separator
        marT={isAtLeastExpandedDisplaySize ? 48 : 40}
        marB={isAtLeastExpandedDisplaySize ? 48 : 40}
      />
      <Container dc fu g={32}>
        <Box dc g={8}>
          <H3 s="XL" fw="semibold">
            Missions
          </H3>
          <Box g={4} ac>
            <img src="/img/icons/warning.svg" alt="warning icon" />
            <P>
              If you are using Coinbase, Bybit or Bitget please connect that
              wallet to receive a multiplier rewards.
              <Strong> You must do this as the first mission</Strong>
            </P>
          </Box>
        </Box>
        <TaskContainer g={24}>
          {multiplierMissions.map((task) => (
            <TaskComponent
              key={`${task.id}-multiplier-mission`}
              isExpired={isExpired}
              task={task}
              projectStatus={projectStatus}
              expiresIn={endTimeLeft}
              isHighlighted
            />
          ))}
          {activeTasksWithoutMultiplierMission.map((task) => (
            <TaskComponent
              key={`${task.id}-active-task`}
              isExpired={isExpired}
              task={task}
              projectStatus={projectStatus}
              expiresIn={endTimeLeft}
            />
          ))}
          {completedTasks.map((task) => (
            <TaskComponent
              key={`${task.id}-completed-task`}
              isExpired={isExpired}
              task={task}
              isComplete
              projectStatus={projectStatus}
              expiresIn={endTimeLeft}
            />
          ))}
          {expiredTasks.map((task) => (
            <TaskComponent
              key={`${task.id}-expired-task`}
              isExpired
              task={task}
              projectStatus={projectStatus}
              expiresIn={endTimeLeft}
            />
          ))}
        </TaskContainer>
        <EmbeddableModal
          dismiss={dismissModal}
          isVisible={!!activeMission}
          minWidth={isFormMission ? 1024 : 470}
          isClipped={!isFormMission}
        >
          {taskId && <TaskBody id={taskId} dismiss={dismissModal} />}
        </EmbeddableModal>
      </Container>
      <Separator
        marT={isAtLeastExpandedDisplaySize ? 48 : 40}
        marB={isAtLeastExpandedDisplaySize ? 48 : 40}
      />
      <Box
        dc={!isAtLeastExpandedDisplaySize}
        g={isAtLeastExpandedDisplaySize ? 72 : 40}
      >
        <Leaderboard projectId={currentProject.id} />
        <DropAbout drop={drop} />
      </Box>
    </FadeIn>
  )
}

export default ChapterView

function TaskComponent({
  task,
  isComplete,
  isExpired,
  projectStatus,
  expiresIn,
  isHighlighted,
}: {
  task: Task
  isComplete?: boolean
  isExpired?: boolean
  projectStatus?: 'ended' | 'active' | 'coming-soon'
  expiresIn?: string
  isHighlighted?: boolean
}) {
  const { internalName: description, multiplierReward, subtasks } = task
  const coreTask = subtasks?.[0]

  const currentProject = useSelector(selectCurrentProject)
  const projectId = currentProject?.id

  const getFormattedReward = useGetFormattedTaskRewardLabels({
    taskId: task.id,
  })

  const startMission = useStartMission(task.id)
  const {
    isRecurringMission,
    isRollupMission,
    isManualAPIRewardTask,
    isZapValueIndexTask,
    isZapLaunchCreatePoolMission,
    isZapLaunchInvestMission,
  } = getMissionTaskType({
    task,
  })
  const actionText = useMissionActionText({ task, isComplete })

  const handleStartMission = useCallback(() => {
    if (isZapLaunchCreatePoolMission) {
      window.open(routes.memecoins.launcher, '_blank')
    } else if (isZapLaunchInvestMission) {
      window.open(routes.memecoins.index, '_blank')
    } else {
      startMission()
    }
  }, [isZapLaunchCreatePoolMission, isZapLaunchInvestMission, startMission])

  if (!coreTask) return <></>

  const { formattedReward, formattedMaxReward, formattedTiers } =
    getFormattedReward({
      // rewardDisplay: 'XP',
      // includePlus: !isComplete && !isManualAPIRewardTask,
      overrideReward: isManualAPIRewardTask && !isComplete ? 0 : undefined,
    })

  if (isRollupMission) {
    return <RollupTaskCard task={task} projectStatus={projectStatus} />
  }

  if (isRecurringMission) {
    return (
      <RecurringTaskCard
        task={task}
        isComplete={isComplete}
        onClick={handleStartMission}
        actionText={actionText}
        projectStatus={projectStatus}
        isExpired={isExpired}
      />
    )
  }

  if (isZapValueIndexTask) {
    return (
      <ZapValueIndexTaskCard
        task={task}
        isComplete={isComplete}
        projectStatus={projectStatus}
        isExpired={isExpired}
        onClick={handleStartMission}
      />
    )
  }

  const projectInactive = projectStatus !== 'active'

  return (
    <SharedTaskCard
      task={task}
      isComplete={isComplete}
      onClick={handleStartMission}
      actionText={actionText}
      projectStatus={projectStatus}
      isFaded={isExpired || isManualAPIRewardTask || isComplete}
      expiresIn={expiresIn}
      isExpired={isExpired}
      multiplier={multiplierReward}
      formattedReward={formattedReward}
      isHighlighted={isHighlighted}
    >
      <Box g={40} ac>
        <Box dc g={6}>
          <TaskDescription>{description}</TaskDescription>
          <Box g={4} ac>
            <Info type="primary">
              <span className="reward-amount">
                <span className="allocation-amount">{formattedReward}</span>
                {formattedMaxReward && (
                  <VariableRewardText>{formattedMaxReward}</VariableRewardText>
                )}
                {formattedTiers && (
                  <VariableRewardText>{formattedTiers}</VariableRewardText>
                )}
              </span>
            </Info>
          </Box>
        </Box>
      </Box>
      <MissionAction taskId={task.id} projectStatus={projectStatus} />
    </SharedTaskCard>
  )
}

const InvestmentContainer = styled(Box)`
  width: 100%;
  position: relative;

  span.title {
    color: #fff;
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 100% */
    text-transform: uppercase;
  }
  span.amount,
  span.currency {
    min-width: 40px;
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.18px;
    text-transform: uppercase;
  }
`

const InvestmentContainerMobile = styled(Box)`
  span.title {
    color: #fff;
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 100% */
    text-transform: uppercase;
  }
  span.amount,
  span.currency {
    min-width: 40px;
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.18px;
    text-transform: uppercase;
  }
`

const AllocPrice = styled(Box)`
  --button-border-color: rgba(255, 255, 255, 0.2);
  border: 1px solid var(--button-border-color);
  padding: 10px 18px;
  width: auto;
  text-align: center;
  justify-content: center;
`

const InfoBlock = styled(Box)<{ isFirst?: boolean }>`
  align-items: flex-start;

  padding-right: 1rem;
  padding-left: 1rem;

  @media screen and (max-width: 1000px) {
    border-left: none;
  }
`

const InfoTitle = styled.span`
  color: #fff;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 100% */
  text-transform: uppercase;

  @media screen and (max-width: 1000px) {
    font-size: 14px;
  }
`

const InfoValue = styled.span`
  color: ${({ theme }) => theme.colors.brandPrimary};
  text-align: center;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.2px;
  text-transform: uppercase;

  display: flex;
  align-items: center;
`

const ProgressToNextLevel = styled.div`
  width: 100%;

  .progress-level,
  .progress-level-remaining {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%; /* 12px */
    letter-spacing: -0.12px;
    text-transform: uppercase;
  }

  .progress-level-remaining {
    color: white;
    // opacity: 0.4;
  }

  .progress-level-remaining .non-bold {
    font-weight: 400;
  }
`

const Desktop = styled.div<{ w?: string }>`
  @media screen and (max-width: 1000px) {
    display: none;
  }
  ${(props) =>
    props.w &&
    css`
      width: ${props.w};
    `}
`

const Mobile = styled.div`
  width: 100%;
  @media screen and (min-width: 1000px) {
    display: none;
  }
`

export const TooltipText = styled.span`
  color: #fff;
  font-variant-numeric: ordinal;
  font-family: 'Space Grotesk';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: -0.56px;
  text-transform: none;
  line-height: 1.6;
`

export function YourInvestment() {
  const xpData = useSelector(selectXpData)

  if (isEmpty(xpData) || isNil(xpData)) return null

  return (
    <>
      <Desktop w="100%">
        <InvestmentContainer afs g={40}>
          <Box dc g={16} afs fu aw className="alloc-block">
            <YourLevel />
          </Box>

          <Box dc g={16} afs fu aw className="alloc-block">
            <TotalXP />
          </Box>

          <InfoBlock jsb dc g={16} isFirst className="hide-mobile">
            <ToNextLevel />
          </InfoBlock>
        </InvestmentContainer>
      </Desktop>
      <Mobile>
        <Box dc ac g={22}>
          <InvestmentContainerMobile ac g={16}>
            <Box dc g={16} ac jc className="alloc-block">
              <YourLevel />
            </Box>

            <Box dc g={16} ac jc className="alloc-block">
              <TotalXP />
            </Box>
          </InvestmentContainerMobile>
          <InvestmentContainerMobile dc g={12}>
            <InfoBlock dc g={16}>
              <ToNextLevel />
            </InfoBlock>
          </InvestmentContainerMobile>
        </Box>
      </Mobile>
    </>
  )
}

export function YourLevel() {
  const xpData = useSelector(selectXpData)

  if (isEmpty(xpData) || isNil(xpData)) return null

  const { level } = xpData

  return (
    <>
      <InfoTitle>Your level</InfoTitle>
      <AllocPrice g={8} afe className="alloc-price">
        <InfoValue>
          <TickUpNumber amount={level} />
        </InfoValue>
      </AllocPrice>
    </>
  )
}

export function TotalXP() {
  const xpData = useSelector(selectXpData)

  if (isEmpty(xpData) || isNil(xpData)) return null

  const { totalXp, chapterRolloverXp } = xpData

  return (
    <>
      <InfoTitle>
        <Box ac g={6} fu aw relative>
          Total XP
          {isNotNil(chapterRolloverXp) && (
            <>
              <img
                src="/img/icons/info-circle.svg"
                alt="info tooltip icon"
                data-tooltip-id="rollover-xp-tooltip"
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: '-30px',
                }}
              />
              <Tooltip
                id="rollover-xp-tooltip"
                opacity={1}
                style={{
                  maxWidth: '200px',
                  zIndex: 1,
                }}
              >
                <TooltipText>
                  You received {chapterRolloverXp.toLocaleString()} XP from
                  participating in the previous chapter.
                </TooltipText>
              </Tooltip>
            </>
          )}
        </Box>
      </InfoTitle>
      <AllocPrice g={8} afe className="alloc-price">
        <InfoValue>
          <TickUpNumber amount={totalXp} />
        </InfoValue>
      </AllocPrice>
    </>
  )
}

export function ToNextLevel() {
  const xpData = useSelector(selectXpData)
  const { totalXp, currentLevelCeiling, currentLevelFloor } = xpData ?? {}

  const { currentXpInLevel, xpToNextLevel, percentage } = useMemo(() => {
    const currentXpInLevel = totalXp - currentLevelFloor
    const xpToNextLevel = currentLevelCeiling - totalXp
    const percentage =
      (currentXpInLevel / (currentLevelCeiling - currentLevelFloor)) * 100

    return { currentXpInLevel, xpToNextLevel, percentage }
  }, [currentLevelCeiling, currentLevelFloor, totalXp])

  if (isEmpty(xpData) || isNil(xpData)) return null

  return (
    <>
      <InfoTitle>Next level</InfoTitle>
      <ProgressToNextLevel>
        <ProgressBar percentage={percentage} />
        <Box g={16} marginTop={8}>
          <Box className="progress-level">{currentXpInLevel} XP</Box>
          <Box className="progress-level-remaining" jfe>
            <div>
              {xpToNextLevel} XP <span className="non-bold">remaining</span>
            </div>
          </Box>
        </Box>
      </ProgressToNextLevel>
    </>
  )
}

function TaskBody({ id, dismiss }: { id: number; dismiss: () => void }) {
  const currentProject = useSelector(selectCurrentProject)

  const task = currentProject?.tasks?.find((task) => task.id === id)

  if (!task) return <></>

  const coreTask = task?.subtasks?.[0]

  if (!coreTask) return <></>

  const isImageVerification = !!coreTask?.imageVerification

  if (isImageVerification)
    return (
      <ImageVerificationTask subtask={coreTask} taskId={id} onClose={dismiss} />
    )

  const isTwitterTask = coreTask?.requiresSocial === 'Twitter'

  if (isTwitterTask) return <TwitterTask subtask={coreTask} taskId={id} />

  const isDiscordTask = coreTask?.requiresSocial === 'Discord'

  if (isDiscordTask) return <DiscordTask subtask={coreTask} taskId={id} />

  const isEmailTask = coreTask?.isEmailTask

  if (isEmailTask) return <EmailTask subtask={coreTask} taskId={id} />

  const isOnChainTask = coreTask?.onChainTask

  if (isOnChainTask) return <OnChainTask subtask={coreTask} taskId={id} />

  const isGraphIndexerTask = coreTask?.newTaskType === NewTaskType.GRAPH_INDEXER

  if (isGraphIndexerTask)
    return <GraphIndexerTask subtask={coreTask} taskId={id} />

  const isZapValueIndexTask =
    coreTask?.newTaskType === NewTaskType.ZAP_VALUE_INDEX

  if (isZapValueIndexTask)
    return <ZapValueIndexTask subtask={coreTask} taskId={id} />

  const isVerifyWalletTask = coreTask?.newTaskType === NewTaskType.VERIFY_WALLET

  if (isVerifyWalletTask)
    return <VerifyWalletTask subtask={coreTask} taskId={id} />

  const isTelegramTask = coreTask?.requiresSocial === 'Telegram'

  if (isTelegramTask) return <TelegramTask subtask={coreTask} taskId={id} />

  const isManualAPIRewardTaskWithLink =
    coreTask?.newTaskType === NewTaskType.MANUAL_API_REWARD &&
    !!(coreTask?.metaData as ManualApiRewardMetaData)?.redirectLink

  if (isManualAPIRewardTaskWithLink)
    return <ManualAPIRewardTask subtask={coreTask} taskId={id} />

  const isFormMission = !!coreTask?.formMissionId

  // const isZapLaunchMission = coreTask?.newTaskType === NewTaskType.ZAP_LAUNCH

  if (isFormMission) {
    const { metaData } = task.subtasks[0]
    let reward = task.pointReward
    let maxReward
    if (metaData && 'rewardPerMetric' in metaData && 'maxReward' in metaData) {
      reward = metaData.rewardPerMetric
      maxReward = metaData.maxReward
    }

    return (
      <FormTaskCard
        subtask={coreTask}
        taskId={id}
        reward={reward}
        maxReward={maxReward}
      />
    )
  }

  const isReferralTask = !!coreTask?.isReferralTask

  if (isReferralTask)
    return (
      <ReferralTask
        subtask={coreTask}
        taskId={id}
        projectId={currentProject.id}
      />
    )

  return <Box dc>TODO - other task types</Box>
}

export function Checkbox({ mt }: { mt?: number }) {
  return (
    <Box c fu aw marginTop={mt}>
      <img src={checkIconSrc} width={20} height={20} alt="check icon" />
    </Box>
  )
}

export function CompletedCheckbox({
  mt,
  w,
  h,
}: {
  mt?: number
  w?: number
  h?: number
}) {
  return (
    <Box c fu aw marginTop={mt}>
      <img
        src="/img/icons/completed.svg"
        width={w ?? 32}
        height={h ?? 32}
        alt="completed icon"
      />
    </Box>
  )
}

export function ExpiredCheckbox({
  mt,
  w,
  h,
}: {
  mt?: number
  w?: number
  h?: number
}) {
  return (
    <Box c fu aw marginTop={mt}>
      <img
        src="/img/icons/expired.svg"
        width={w ?? 32}
        height={h ?? 32}
        alt="completed icon"
      />
    </Box>
  )
}

const RewardPill = styled(Box)`
  padding: var(--units-6, 6px);

  border-radius: 24px;
  background: var(--surface-minimal-secondary, rgba(255, 255, 255, 0.08));
`

const ProjectInformationBox = styled(Box)`
  ${boxGapGuttersCssRules}

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.medium}px) {
    flex-direction: column;
  }
`

const ProjectImageBox = styled(Box)`
  max-width: 50%;

  img {
    width: 100%;
    max-width: 544px;
    height: 368px;
    object-fit: cover;
    border-radius: 24px;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.medium}px) {
    max-width: 100%;

    img {
      max-width: 100%;
      width: calc(100vw);
    }
  }
`
